import { useState, useCallback, useMemo } from "react";

import { UNSTRICT_EMAIL_REGEX, EMAIL_SEPARATION_REGEX } from "./constants";
import { pruneDuplicates, filterValidEmails } from "./utils";
import type { MultiEmailInputProps } from "./types";

export function useMultiEmailState(props: MultiEmailInputProps) {
  const {
    value = [],
    onChange,
    onBlur,
    visibleEmailsCount = 3,
    isAlwaysExpanded = false,
    counter,
    filterInvalidEmails,
  } = props;

  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [duplicateEmails, setDuplicateEmails] = useState<string[]>([]);

  const addEmails = useCallback(
    (input: string) => {
      const trimmed = input.trim();
      if (!trimmed) return;
      UNSTRICT_EMAIL_REGEX.lastIndex = 0;
      EMAIL_SEPARATION_REGEX.lastIndex = 0;
      const emailMatches =
        trimmed.match(UNSTRICT_EMAIL_REGEX) ||
        trimmed.match(EMAIL_SEPARATION_REGEX) ||
        [];
      if (emailMatches.length === 0) return;
      const { uniqueEmails, duplicates } = pruneDuplicates(value, emailMatches);
      onChange?.(uniqueEmails);
      setDuplicateEmails(duplicates);
      setInputValue("");
    },
    [value, onChange]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!inputValue && event.key === "Backspace" && value.length > 0) {
        onChange?.(value.slice(0, -1));
        return;
      }
      if (!inputValue) return;
      if (["Enter", "Tab", ",", " "].includes(event.key)) {
        addEmails(inputValue);
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [inputValue, value, onChange, addEmails]
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const text = event.clipboardData.getData("text");
      if (!text) return;
      event.preventDefault();
      addEmails(text);
    },
    [addEmails]
  );

  const handleBlur = useCallback(() => {
    if (inputValue) {
      addEmails(inputValue);
    } else {
      onBlur?.();
    }
    setIsFocused(false);
    setDuplicateEmails([]);
  }, [inputValue, addEmails, onBlur]);

  const handleFocus = useCallback(() => setIsFocused(true), []);

  const handleChipRemove = useCallback(
    (emailValue: string) =>
      onChange?.(value.filter(e => e.value !== emailValue)),
    [value, onChange]
  );

  const handleFilterInvalidEmails = useCallback(
    () => onChange?.(filterValidEmails(value)),
    [value, onChange]
  );

  const validCount = useMemo(() => value.filter(e => e.valid).length, [value]);
  const isCounterVisible =
    !!counter &&
    (typeof counter === "boolean" ||
      !counter.startsFrom ||
      validCount >= counter.startsFrom);
  const shouldCollapse =
    !isAlwaysExpanded && !isFocused && value.length > visibleEmailsCount;
  const visibleEmails = shouldCollapse
    ? value.slice(0, visibleEmailsCount)
    : value;
  const hiddenCount = shouldCollapse ? value.length - visibleEmailsCount : 0;
  const isFilterLinkVisible =
    !!filterInvalidEmails && value.length > validCount;

  return {
    inputValue,
    setInputValue,
    isFocused,
    duplicateEmails,
    handleKeyDown,
    handlePaste,
    handleBlur,
    handleFocus,
    handleChipRemove,
    handleFilterInvalidEmails,
    isCounterVisible,
    validCount,
    visibleEmails,
    hiddenCount,
    isFilterLinkVisible,
  };
}
